import { brands } from '@trmediaab/zebra-utils';
import { useMemo } from 'react';
import xss, { whiteList } from 'xss';

const reLink = /<a /gi;
const reHref = /(href=["'](.*?)["'])/i;

/**
 * Sanitize HTML and add `rel="noopener noreferrer"` to all links that are not
 * a link to a TR Media property.
 */
const useSafeHtml = (html: string, canonicalRoot?: string): string =>
  useMemo(() => {
    const canonicalReferrer = canonicalRoot || process.env.CANONICAL_ROOT;
    const allowedReferrers = brands.getBrands().map(brand => brand.href);
    if (
      canonicalReferrer != null &&
      !allowedReferrers.includes(canonicalReferrer)
    ) {
      allowedReferrers.push(canonicalReferrer);
    }

    // Add `id` and `class` as a whitelisted attribute on all elements
    const extendedWhiteList = Object.keys(whiteList).reduce<typeof whiteList>(
      (acc, key) => {
        const value = whiteList[key];
        acc[key] = value != null ? [...value, 'id', 'class'] : ['id', 'class'];
        return acc;
      },
      {},
    );

    return xss(html, {
      whiteList: extendedWhiteList,
      onTag: (tag, html, options) => {
        // Prevent referrer leaking to non TR hosts
        if (tag === 'a' && !options.isClosing && reHref.test(html)) {
          const href = html.match(reHref)?.[2];
          if (
            href != null &&
            !allowedReferrers.some((allowed: string) =>
              href.startsWith(allowed),
            )
          ) {
            return html.replaceAll(
              reLink,
              `<a rel="noopener noreferrer" target="_blank" `,
            );
          }
        } else if (tag === 'li') {
          // Wrap li elements in a div to prevent list styles from leaking
          return !options.isClosing ? `${html}<div>` : `</div>${html}`;
        }
      },
    });
  }, [canonicalRoot, html]);

export default useSafeHtml;
